import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable, {
  headerCellStyle,
  bodyCellStyle,
} from "./components/SelectableEnrollmentTable";

interface BulkAddSubBatchModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "added" | "skipped" | "not_found" | "error";
  reason?: string;
  error?: string;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    added:     { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkAddSubBatchModal: React.FC<BulkAddSubBatchModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const [batches, setBatches] = useState<
    Array<{ id: number; name: string; batchTypeId?: number | null }>
  >([]);
  const [batchCourses, setBatchCourses] = useState<
    Array<{ batchId: number; courseId: number | null; programId: number | null }>
  >([]);
  const [courses, setCourses] = useState<Array<{ id: number; name: string }>>([]);
  const [programs, setPrograms] = useState<Array<{ id: number; name: string }>>([]);
  const [isProgramBased, setIsProgramBased] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [courseId, setCourseId] = useState<number | "">("");
  const [subBatchId, setSubBatchId] = useState<number | "">("");
  const [date, setDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  // Load the batch list (used as the sub-batch picker) once when the modal opens.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingBatches(true);
    EnrollmentService.getLookups()
      .then((resp) => {
        if (cancelled) return;
        const d = resp.data?.data || {};
        setBatches(d.batches || []);
        setBatchCourses(d.batchCourses || []);
        setCourses(d.courses || []);
        setPrograms(d.programs || []);
        setIsProgramBased(!!d.isProgramBased);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load batches");
      })
      .finally(() => {
        if (!cancelled) setLoadingBatches(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const {
    enrollments,
    selectedIds,
    selectedEnrollmentIds,
    toggle,
    toggleAll,
    allSelected,
  } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  // Sub-batches (BatchTypeId === 2) mapped to the chosen course/program, mirroring
  // the legacy Course → Batch cascade (getSubBatchesByCourse).
  const availableSubBatches = useMemo(() => {
    if (!courseId) return [];
    const okBatchIds = new Set<number>();
    for (const bc of batchCourses) {
      const key = isProgramBased ? bc.programId : bc.courseId;
      if (key === courseId) okBatchIds.add(bc.batchId);
    }
    return batches.filter((b) => b.batchTypeId === 2 && okBatchIds.has(b.id));
  }, [batches, batchCourses, isProgramBased, courseId]);

  const onCourseChange = (v: number | "") => {
    setCourseId(v);
    setSubBatchId("");
  };

  const handleClose = () => {
    setCourseId("");
    setSubBatchId("");
    setDate("");
    setRemarks("");
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!courseId) {
      setError("Pick a course.");
      return;
    }
    if (!subBatchId) {
      setError("Pick a batch.");
      return;
    }
    if (!date) {
      setError("Date is required.");
      return;
    }
    if (!remarks.trim()) {
      setError("Remarks are required.");
      return;
    }
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkAddSubBatch({
        enrollmentIds: selectedEnrollmentIds,
        subBatchId: Number(subBatchId),
        date: date || null,
        remarks: remarks.trim() || null,
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk add sub-batch failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? "Attach Sub Batch — Results" : "Attach Sub Batch"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Form.Group className="mb-3" style={{ flex: "1 1 240px", minWidth: 0 }}>
                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                  {isProgramBased ? "Program" : "Course"}{" "}
                  <span style={{ color: "var(--status-danger)" }}>*</span>
                </Form.Label>
                <Form.Select
                  className="form-control"
                  size="sm"
                  value={courseId}
                  onChange={(e) => onCourseChange(e.target.value ? Number(e.target.value) : "")}
                  disabled={loadingBatches}
                  style={{ width: "100%" }}
                >
                  <option value="">
                    {loadingBatches ? "Loading…" : "--Select--"}
                  </option>
                  {(isProgramBased ? programs : courses).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: "1 1 240px", minWidth: 0 }}>
                <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                  Batch <span style={{ color: "var(--status-danger)" }}>*</span>
                </Form.Label>
                <Form.Select
                  className="form-control"
                  size="sm"
                  value={subBatchId}
                  onChange={(e) => setSubBatchId(e.target.value ? Number(e.target.value) : "")}
                  disabled={!courseId}
                  style={{ width: "100%" }}
                >
                  <option value="">
                    {!courseId
                      ? `Select a ${isProgramBased ? "program" : "course"} first`
                      : availableSubBatches.length === 0
                      ? "No sub-batches for this course"
                      : "--Select--"}
                  </option>
                  {availableSubBatches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <Form.Group className="mb-3" style={{ width: 180 }}>
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                Date <span style={{ color: "var(--status-danger)" }}>*</span>
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                Remarks <span style={{ color: "var(--status-danger)" }}>*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Form.Group>

            <SelectableEnrollmentTable
              enrollments={enrollments}
              selectedIds={selectedIds}
              allSelected={allSelected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              disabled={submitting}
              caption={`Selected Students (${selectedEnrollmentIds.length} of ${enrollments.length} selected) — added to the sub-batch (existing memberships kept)`}
            />
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.added ?? 0}</strong> added
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "added" && (
                          <span style={{ color: "var(--text-body)" }}>Added to sub-batch</span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_in_sub_batch"
                              ? "Already in this sub-batch"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                submitting ||
                !courseId ||
                !subBatchId ||
                !date ||
                !remarks.trim() ||
                selectedEnrollmentIds.length === 0
              }
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Submitting…
                </>
              ) : (
                <>Submit</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAddSubBatchModal;
